import type { TDateISO } from "~/@types/TDateISO";
import type { ValidationError } from "~/gql/graphqlBase.generated";

import { TypedDocumentString } from "~/gql/graphqlBase.generated";
import {
	CreateMakeToOrderPlanForLinesMutation,
	CreateMakeToOrderPlanMutation
} from "~/gql/mutations/produce/makeToOrderPlan.generated";
import {
	AdjustProductionLotAllocationMutation,
	AllocateProductionLotToOrderLineMutation,
	CancelProductionLotAllocationMutation
} from "~/gql/mutations/produce/productionLotAllocation.generated";
import {
	GetOpenOrdersForLotItemQuery,
	GetOrderLinesForLotItemQuery,
	GetOrderProductionLotAllocationByIdQuery,
	GetOrderProductionLotAllocationsQuery,
	GetProductionLotOpenAllocationsQuery
} from "~/gql/queries/produce/productionLotAllocation.generated";
import { baseApiWithGraphql } from "~/services/baseApi";

export interface MakeToOrderPlanTimingLineInput {
	containerQuantity?: number | null;
	orderLineId: string;
	productionItemId?: string | null;
	quantity: number;
}

export interface MakeToOrderPlanTimingInput {
	customerId?: string | null;
	lines: MakeToOrderPlanTimingLineInput[];
	locationId?: string | null;
	orderId: string;
	preFinished: boolean;
	productionPlanId: string;
}

export type PreviewMakeToOrderPlanTimingQueryVariables = { input: MakeToOrderPlanTimingInput };

export interface MakeToOrderPlanLineTiming {
	errors?: Array<Pick<ValidationError, "code" | "message">> | null;
	finishGrowWeeks?: number | null;
	orderLineId: string;
	plugStartWeek?: TDateISO | null;
	shipWeek?: TDateISO | null;
	transplantWeek?: TDateISO | null;
}

export type PreviewMakeToOrderPlanTimingQuery = { previewMakeToOrderPlanTiming: MakeToOrderPlanLineTiming[] };

export const PreviewMakeToOrderPlanTimingDocument = new TypedDocumentString<
	PreviewMakeToOrderPlanTimingQuery,
	PreviewMakeToOrderPlanTimingQueryVariables
>(`
    query PreviewMakeToOrderPlanTiming($input: MakeToOrderPlanTimingInput!) {
  previewMakeToOrderPlanTiming(input: $input) {
    orderLineId
    finishGrowWeeks
    transplantWeek
    shipWeek
    plugStartWeek
    errors {
      code
      message
    }
  }
}
    `);

const makeToOrderPlanTimingEndpoints = baseApiWithGraphql.injectEndpoints({
	endpoints: (build) => ({
		PreviewMakeToOrderPlanTiming: build.query<
			PreviewMakeToOrderPlanTimingQuery,
			PreviewMakeToOrderPlanTimingQueryVariables
		>({ query: (variables) => ({ document: PreviewMakeToOrderPlanTimingDocument, variables }) })
	})
});

export const { usePreviewMakeToOrderPlanTimingQuery } = makeToOrderPlanTimingEndpoints;

export const productionLotAllocationEndpoints = {
	GetProductionLotOpenAllocations: {
		providesTags: ["ProductionLotAllocations"] as AddTagTypes,
		transformResponse: (res: GetProductionLotOpenAllocationsQuery) => res
	},
	AdjustProductionLotAllocation: {
		invalidatesTags: ["ProductionLotAllocations", "ProductionLots"] as AddTagTypes,
		transformResponse: (res: AdjustProductionLotAllocationMutation) => res
	},
	AllocateProductionLotToOrderLine: {
		invalidatesTags: ["ProductionLotAllocations", "ProductionLots"] as AddTagTypes,
		transformResponse: (res: AllocateProductionLotToOrderLineMutation) => res
	},
	CancelProductionLotAllocation: {
		invalidatesTags: ["ProductionLotAllocations", "ProductionLots"] as AddTagTypes,
		transformResponse: (res: CancelProductionLotAllocationMutation) => res
	},
	CreateMakeToOrderPlanForLines: {
		invalidatesTags: ["ProductionLotAllocations", "ProductionLots"] as AddTagTypes,
		transformResponse: (res: CreateMakeToOrderPlanForLinesMutation) => res
	},
	CreateMakeToOrderPlan: {
		invalidatesTags: ["ProductionLotAllocations", "ProductionLots"] as AddTagTypes,
		transformResponse: (res: CreateMakeToOrderPlanMutation) => res
	},
	GetOpenOrdersForLotItem: {
		providesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: GetOpenOrdersForLotItemQuery) => res
	},
	GetOrderLinesForLotItem: {
		providesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: GetOrderLinesForLotItemQuery) => res
	},
	GetOrderProductionLotAllocationById: {
		providesTags: ["ProductionLotAllocations"] as AddTagTypes,
		transformResponse: (res: GetOrderProductionLotAllocationByIdQuery) => res
	},
	GetOrderProductionLotAllocations: {
		providesTags: ["ProductionLotAllocations"] as AddTagTypes,
		transformResponse: (res: GetOrderProductionLotAllocationsQuery) => res
	}
};
